import config from "./config/config";

export const capitalize = (s: string, onlyFirstLetter = false): string => {
  if (s.length === 0) {
    return "";
  }

  if (onlyFirstLetter) {
    return s.charAt(0).toUpperCase() + s.slice(1);
  }
  return s.charAt(0).toUpperCase() + s.slice(1).toLocaleLowerCase();
};

/**
 * Capitalizes a backend-provided title for standalone display. Those arrive
 * lowercase so they compose mid-sentence ("Scan your residence permit"), which
 * leaves headings and option labels to capitalize them back.
 *
 * Brand-style names that open with a lowercase letter before a capital
 * ("e-Visa", "eKITAS card") are left alone.
 */
export const capitalizeTitle = (title: string): string =>
  /^[a-z]-?[A-Z]/.test(title) ? title : capitalize(title, true);

export const normalizeName = (name: string) => {
  if (name.length === 0) {
    return "";
  }

  return name
    .split(/(-| )/)
    .map((s) => capitalize(s))
    .join("");
};

export const getDateWithoutTime = (date: Date): string => {
  let month = "" + (date.getUTCMonth() + 1);
  let day = "" + date.getUTCDate();
  const year = date.getUTCFullYear();

  if (month.length < 2) {
    month = "0" + month;
  }
  if (day.length < 2) {
    day = "0" + day;
  }

  return `${year}-${month}-${day}`;
};

/**
 * Formats an ISO `YYYY-MM-DD` date string per a locale's rules
 * (e.g. `22/02/1966` for `en-GB`/`es-MX`, `02/22/1966` for `en-US`).
 *
 * Defaults to `navigator.language` — the browser UI language, which is what
 * native `<input type="date">` fields actually follow — so the rendered date
 * matches those inputs. Note: `Intl.DateTimeFormat()` with no locale defaults to
 * `en-US` in Chrome regardless of the browser language, so we must pass
 * `navigator.language` explicitly to track the input. Numeric parts are pinned
 * to 2 digits for a stable zero-padded `dd/mm/yyyy`-style output.
 *
 * The ISO string is parsed as UTC midnight, so we format in the UTC timezone to
 * keep the day from shifting in negative-offset timezones. Returns "" for an
 * empty value and the original string if it can't be parsed.
 */
export const formatIsoDateToLocale = (
  isoDate: string,
  locale: string = navigator.language
): string => {
  if (!isoDate) {
    return "";
  }

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }

  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
};

// eslint-ignore- no-useless-escape
export function isMobileDevice() {
  let check = false;
  (function (a) {
    /* cSpell:disable */
    if (
      /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
        a
      ) ||
      // eslint-disable-next-line
      /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
        a.substr(0, 4)
      )
    )
      /* cSpell:enable */
      check = true;
  })(navigator.userAgent || navigator.vendor);
  return check;
}

// What an ARTIFACT you have found, dear traveler!
// Dec 2022: Ha-ha, I did! I'm so happy!
export function zeroPad(num: number, places: number) {
  const zero = places - num.toString().length + 1;
  return Array(+(zero > 0 && zero)).join("0") + num;
}

export function initialLocale(): string {
  const location = window.location;
  const searchParams = new URLSearchParams(location.search);
  const locale: string | null = searchParams.get("locale");

  if (locale) {
    // Exact match: honour the URL locale.
    if (config.supportedLocales.includes(locale)) {
      return locale;
    }

    // No exact match: fall back to a supported locale in the same language if we
    // ship one (e.g. "es-ES" -> "es-MX"), so a passenger who passes an
    // unsupported region code still gets their language. Without this, the
    // unsupported value would be stored verbatim: content silently falls back to
    // English while the locale picker shows the raw "locale.<code>" key with no
    // flag.
    const language = locale.split("-")[0].toLowerCase();
    const sameLanguageLocale = config.supportedLocales.find(
      (supported) => supported.split("-")[0].toLowerCase() === language
    );
    if (sameLanguageLocale) {
      return sameLanguageLocale;
    }
  }

  return config.defaultLocale;
}

export class LocalDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
  constructor(year: number, month: number, day: number) {
    if (month < 1 || month > 12) {
      throw new Error(`Invalid month ${month}`);
    }
    if (day < 1 || day > 31) {
      throw new Error(`Invalid day ${day}`);
    }
    this.year = year;
    this.month = month;
    this.day = day;
  }

  toIsoString(): string {
    return `${this.year}-${zeroPad(this.month, 2)}-${zeroPad(this.day, 2)}`;
  }

  /**
   * returns the Date that has the same date as LocalDate in UTC timezone and 00:00:00 time
   * compatible with Date.toISOString():
   *
   * @example
   * // returns true
   * val localDate = new LocalDate(2020, 12, 31)
   * localDate.toIsoString() === localDate.toUtcDate().toISOString().split("T")[0]
   */
  toUtcDate(): Date {
    return new Date(this.toIsoString());
  }

  static fromIsoString(isoString: string): LocalDate {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoString)) {
      throw new Error(
        `Invalid date string ${isoString}. It must be in YYYY-MM-DD format`
      );
    }
    const [year, month, day] = isoString.split("-");
    return new LocalDate(Number(year), Number(month), Number(day));
  }

  /**
   * creates the LocalDate with the same date in local Timezone
   * use it to get date from Date object created with `new Date()` or `new Date(year, month, date)`
   * because stores given date in local timezone
   */
  static fromDateInLocalTimezone(date: Date): LocalDate {
    return new LocalDate(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  }

  /** creates the LocalDate with the same date in UTC Timezone
   * use it to get date from Date object created with `Date(isoDateString)`
   * because it stores given date in UTC
   *
   * @example
   * // returns true
   * const isoDateString = "2020-12-31"
   * LocalDate.fromIsoString(isoDateString) === LocalDate.fromUtcDate(new Date(isoDateString))
   */
  static fromUtcDate(date: Date): LocalDate {
    return LocalDate.fromIsoString(date.toISOString().substring(0, 10));
  }
}

export function mrzToLocalDate(
  dateString: string,
  century?: 19 | 20
): LocalDate {
  // assert dateString is a 6 digit string
  if (!/^\d{6}$/.test(dateString)) {
    throw new Error(`Invalid date string: ${dateString}`);
  }
  const [year, month, day] = dateString.match(/\d{2}/g)!.map(Number);

  const fullYear = (century ?? guessCentury(year)) * 100 + year;

  return new LocalDate(fullYear, month, day);
}

/**
 * Guess the century of a year. Return 20th century if year is not more than 10 years from now, 19th otherwise.
 * @param year Year to guess the century for
 */
function guessCentury(year: number): 19 | 20 {
  if (year < 0 || year > 99) throw new Error("Year must be between 0 and 99");

  // get current 2-digit year
  const currentYear = new Date().getFullYear() % 100;

  // if date is more than 10 years in the future, assume it is from the past century
  return year > currentYear + 15 ? 19 : 20;
}

export function enumFromStringValue<T>(
  enm: { [s: string]: T },
  value: string
): T | undefined {
  return (Object.values(enm) as unknown as string[]).includes(value)
    ? (value as unknown as T)
    : undefined;
}

export function delay(t: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, t);
  });
}

export function daysBeforeDate(date: Date) {
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  const now = new Date();
  return Math.floor(Math.abs((date.getTime() - now.getTime()) / oneDay));
}

export function hoursBeforeDate(date: Date) {
  const oneHour = 60 * 60 * 1000; // minutes*seconds*milliseconds
  const now = new Date();
  return Math.floor(Math.abs((date.getTime() - now.getTime()) / oneHour));
}

export function getParameterByName(name: string) {
  try {
    return new URL(window.location.href).searchParams.get(name);
  } catch (e) {
    return null;
  }
}

/** Languages the zamna.com pax & privacy-notice pages are localized into. */
const ZAMNA_PAGE_LANGS = ["en", "es", "fr", "pt"];

/**
 * Resolves a `{{lang}}` placeholder in an external-link URL to the app locale's
 * language subtag (e.g. "es-MX" -> "es"), limited to the languages the
 * zamna.com pages support and falling back to English for anything they don't
 * (e.g. Arabic). URLs without the placeholder (e.g. a client's own privacy PDF)
 * are returned unchanged.
 */
export function withPageLang(url: string, locale: string): string {
  const subtag = locale.slice(0, 2).toLowerCase();
  const lang = ZAMNA_PAGE_LANGS.includes(subtag) ? subtag : "en";
  return url.replace("{{lang}}", lang);
}
